import { useEffect, useState } from "react";
import { Edit } from "lucide-react";
import { MenuItem, Select, Box } from "@mui/material";
import AnimatedSnackbar from "../../STMPages/components/snackbars/AnimatedSnackbar";
import {
  getAllNationalities,
  getCountries,
  getStates,
  getCities,
  getLocationByZip,
  getMaritalStatus,
  savePrimaryInfo,
} from "../api/ProfileAPI";
import ConfirmDialog from "./common/ConfirmDialog";
import SuccessDialog from "./common/SuccessDialog";

const genderMap = { 0: "Male", 1: "Female", 2: "Other" };

const PrimaryInformationView = ({ profileData, setIsEditing }) => {
  const renderRow = (label, value) => (
    <div className="grid grid-cols-[150px_1fr] py-1 font-altisunisa">
      <span className="text-[#727271] font-medium">{label}:</span>
      <span className="text-black">{value || "-"}</span>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-medium text-[#572575] font-siromi">
          Basic Information
        </h2>
        <button
          onClick={() => setIsEditing(true)}
          className="flex items-center text-[#DF252A] font-bold"
        >
          <Edit className="h-5 w-5 mr-1" /> Edit
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 border-b pb-4">
        {renderRow("First Name", profileData.first_name)}
        {renderRow("Last Name", profileData.last_name)}
        {renderRow("Email", profileData.email)}
        {renderRow(
          "Mobile",
          `  +${profileData.mobile_country_code} ${profileData.mobile}`
        )}
        {renderRow(
          "Alternate Mobile",
          `  +${profileData.secondary_mobile_country_code || ""} ${
            profileData.secondary_mobile || ""
          }`
        )}
        {renderRow("Date of Birth", profileData.date_of_birth)}
        {renderRow("Nationality", profileData.nationality_name)}
        {renderRow("Gender", genderMap[profileData.gender] || "-")}
        {renderRow("Marital Status", profileData.maritialStatusName || "-")}
      </div>

      <h2 className="text-lg font-medium text-[#572575] mt-6 mb-2 font-siromi">
        Residence
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 border-b pb-4">
        {renderRow("Street Address", profileData.address)}
        {renderRow("City", profileData.city_name)}
        {renderRow("State", profileData.state_name)}
        {renderRow("Country", profileData.country_name)}
        {renderRow("Zipcode", profileData.zip_code)}
      </div>

      <h2 className="text-lg font-medium text-[#572575] mt-6 mb-2 font-siromi">
        Advanced Information
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {renderRow("Passport Number", profileData.passport_number)}
        {renderRow("Passport Expiry", profileData.passport_expiry)}
      </div>
    </div>
  );
};

const PrimaryInformationEdit = ({
  profileData,
  setIsEditing,
  handleInputChange,
  onSaveSuccess,
}) => {
  const [isdCodeList, setIsdCodeList] = useState([]);
  const [primaryIsd, setPrimaryIsd] = useState("91"); // default +91
  const [altIsd, setAltIsd] = useState("91");
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const closeToast = () => setToast({ ...toast, open: false });
  const [nationalityList, setNationalityList] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(
    profileData.country_id || ""
  );
  const [selectedState, setSelectedState] = useState(
    profileData.state_id || ""
  );
  const [selectedCity, setSelectedCity] = useState(profileData.city_id || "");
  const [maritalStatusList, setMaritalStatusList] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;
  const showError = (msg) => {
    setToast({ open: true, message: msg, severity: "error" });
    return false;
  };

  const normalizedGender = genderMap[profileData.gender] || profileData.gender;

  const handleCancel = () => {
    setShowConfirm(false); // Only close confirm, keep form open
  };
  const handleConfirm = () => {
    setShowConfirm(false);
    handleSave();
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
  };

  useEffect(() => {
    fetch(`${API_ENDPOINT}countries`)
      .then((r) => r.json())
      .then((data) => {
        const sorted = [...data].sort((a, b) => a.phone_code - b.phone_code);
        const unique = sorted.filter(
          (o, i, self) =>
            i ===
            self.findIndex(
              (t) => t.phone_code === o.phone_code && o.phone_code !== null
            )
        );
        setIsdCodeList(unique);
        const has91 = unique.find((c) => c.phone_code === 91);
        if (!has91 && unique.length) setPrimaryIsd(unique[0].phone_code);
      })
      .catch((err) => console.error("Error fetching country codes:", err));
  }, []);

  const fetchMaritalStatus = async () => {
    try {
      const res = await getMaritalStatus();
      if (res?.success) {
        setMaritalStatusList(res.data || []);
      }
    } catch (err) {
      console.error("Error fetching marital statuses:", err);
    }
  };

  const fetchNationalities = async () => {
    const res = await getAllNationalities();
    setNationalityList(res.data || []);
  };
  const fetchCountries = async () => {
    const res = await getCountries();
    setCountries(res.data || []);
  };

  const handleCountryChange = async (e) => {
    const countryId = e.target.value;
    setSelectedCountry(countryId);
    setStates([]);
    setCities([]);
    setSelectedState("");
    setSelectedCity("");
    if (countryId !== selectedCountry) handleInputChange("zip_code", "");
    handleInputChange("country_id", countryId);

    if (!countryId) return;

    const res = await getStates(countryId);
    setStates(res.data || []);
  };

  const handleStateChange = async (e) => {
    const stateId = e.target.value;
    setSelectedState(stateId);
    setCities([]);
    setSelectedCity("");
    handleInputChange("state_id", stateId);

    if (!stateId) return;
    let payload = { country_id: selectedCountry, state_id: stateId };
    const res = await getCities(payload);
    setCities(res.data || []);
  };

  // ---- When city changes ----
  const handleCityChange = (e) => {
    const cityId = e.target.value;
    setSelectedCity(cityId);
    handleInputChange("city_id", cityId);
  };

  // ---- Handle ZIP for India ----
  const handleZipBlur = async () => {
    const zip = profileData.zip_code?.trim();
    if (!zip || zip.length !== 6) return;

    // Ensure India or selected country is India
    const india = countries.find(
      (c) => c.country_name === "India" || c.id === Number(selectedCountry)
    );
    if (!india || india.country_name !== "India") return;

    try {
      const res = await getLocationByZip(zip);
      if (res.status === 200 && res.data) {
        const { state_id, city_id } = res.data;

        // --- Set country if not already selected ---
        if (!selectedCountry || Number(selectedCountry) !== india.id) {
          setSelectedCountry(india.id);
          handleInputChange("country_id", india.id);
        }

        // --- Set State ---
        if (state_id?.id) {
          setSelectedState(state_id.id);
          handleInputChange("state_id", state_id.id);

          // Update state dropdown if not already populated
          if (!states.length) {
            setStates([{ id: state_id.id, state_name: state_id.state_name }]);
          }
        }

        // --- Handle Cities ---
        if (city_id) {
          // City is directly provided
          setSelectedCity(city_id.id);
          handleInputChange("city_id", city_id.id);

          if (!cities.length) {
            setCities([{ id: city_id.id, city_name: city_id.city_name }]);
          }
        } else if (state_id?.id) {
          // No city in response → fetch cities for the state
          const cityRes = await getCities({
            country_id: india.id,
            state_id: state_id.id,
          });
          if (cityRes?.data?.length) {
            setCities(cityRes.data);
          } else {
            setCities([]); // no cities found
          }
        }
      }
    } catch (err) {
      console.error("ZIP lookup error:", err);
    }
  };

  useEffect(() => {
    fetchNationalities();
    fetchCountries();
    fetchMaritalStatus();
  }, []);

  // When entering edit mode or when profileData/countries change
  useEffect(() => {
    if (profileData.country_id) {
      setSelectedCountry(profileData.country_id);
    }
    if (profileData.state_id) {
      setSelectedState(profileData.state_id);
    }
    if (profileData.city_id) {
      setSelectedCity(profileData.city_id);
    }
  }, [profileData.country_id, profileData.state_id, profileData.city_id]);

  // Fetch states when country preselected
  useEffect(() => {
    if (selectedCountry) {
      getStates(selectedCountry).then((res) => setStates(res.data || []));
    }
  }, [selectedCountry]);

  // Fetch cities when state preselected
  useEffect(() => {
    if (selectedCountry && selectedState) {
      getCities({ country_id: selectedCountry, state_id: selectedState }).then(
        (res) => setCities(res.data || [])
      );
    }
  }, [selectedCountry, selectedState]);

  const handleSave = async () => {
    const selectedNationality = nationalityList.find(
      (n) => n.id === Number(profileData.nationality_id)
    );
    // Name validation: alphabets only
    const nameRegex = /^[A-Za-z]+$/;
    if (
      !profileData.first_name ||
      !nameRegex.test(profileData.first_name.trim())
    ) {
      return showError("First Name should contain only alphabets.");
    }
    if (
      profileData.last_name &&
      !nameRegex.test(profileData.last_name.trim())
    ) {
      return showError("Last Name should contain only alphabets.");
    }

    if (!profileData.date_of_birth) {
      return showError("Enter date of birth");
    }

    if (!profileData.nationality_id) {
      return showError("Please select a Nationality.");
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!profileData.email || !emailRegex.test(profileData.email.trim())) {
      return showError("Please enter a valid email address.");
    }

    // Mobile validation: numbers only
    const phoneRegex = /^[0-9]+$/;
    if (!phoneRegex.test(profileData.mobile)) {
      return showError("Primary Mobile should contain only digits.");
    }

    if (!selectedCountry) return showError("Please select a Country.");
    if (!profileData.zip_code?.trim())
      return showError("Zip/Postal code is required.");
    if (!selectedState) return showError("Please select a State.");
    // if (!selectedCity) return showError("Please select a City.");

    // Build payload if all validations pass
    const payload = {
      user_id: profileData.sp_user_id,
      first_name: profileData.first_name.trim(),
      last_name: profileData.last_name.trim(),
      email: profileData.email.trim(),
      date_of_birth: profileData.date_of_birth,
      passport_number: profileData.passport_number || "",
      passport_expiry: profileData.passport_expiry || "",
      country_id: selectedCountry,
      state_id: selectedState,
      city_id: selectedCity,
      zip_code: profileData.zip_code.trim(),
      address: profileData.address || "",
      nationality_id: selectedNationality?.id,
      gender: profileData.gender,
      maritial_status: profileData.maritial_status,
      secondary_mobile: profileData.secondary_mobile,
      secondary_mobile_country_code: altIsd,
      mobile: profileData.mobile,
    };
    try {
      const res = await savePrimaryInfo(payload);

      if (res?.success) {
        setShowSuccess(true);
      } else {
        showError(res?.message || "Failed to save profile.");
      }
    } catch (err) {
      showError("Something went wrong while saving.");
    }
  };

  const todayStr = new Date().toISOString().split("T")[0];
  const maxDob = new Date();
  maxDob.setFullYear(maxDob.getFullYear() - 13);
  const maxDobStr = maxDob.toISOString().split("T")[0];

  const renderInput = (
    label,
    field,
    type = "text",
    required = false,
    disabled = false
  ) => {
    let extraProps = {};
    if (field === "date_of_birth") extraProps.max = maxDobStr;
    if (field === "passport_expiry") extraProps.min = todayStr;

    return (
      <div className="mb-4 font-altisunisa">
        <label className="block text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
          type={type}
          value={profileData[field] || ""}
          onChange={(e) => handleInputChange(field, e.target.value)}
          disabled={disabled}
          placeholder={`Enter ${label}`}
          {...extraProps}
          className={`w-full px-3 py-2 rounded-md border border-[#EAEBEB] bg-[#F4F5F6] ${
            disabled
              ? "cursor-not-allowed text-gray-400"
              : "text-[#9D0385] font-bold"
          }`}
        />
      </div>
    );
  };

  const renderPhoneField = (label, field, isPrimary = false) => (
    <div className="mb-4 font-altisunisa">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <Select
          value={isPrimary ? primaryIsd : altIsd}
          onChange={(e) =>
            isPrimary
              ? setPrimaryIsd(e.target.value)
              : setAltIsd(e.target.value)
          }
          disabled={isPrimary} // disable primary dropdown
          size="small"
          className={`bg-[#F4F5F6] rounded-md ${
            isPrimary ? "cursor-not-allowed" : ""
          }`}
          sx={{
            "& .MuiInputBase-input": {
              color: "#9D0385",
              fontWeight: "bold",
            },
            minWidth: 90,
          }}
        >
          {isdCodeList.map((option) => (
            <MenuItem key={option.locale_code} value={option.phone_code}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <img
                  src={`https://flagcdn.com/w40/${option.locale_code.toLowerCase()}.png`}
                  alt={option.label}
                  style={{
                    width: "20px",
                    height: "15px",
                    marginRight: "8px",
                    borderRadius: "2px",
                  }}
                />
                +{option.phone_code}
              </Box>
            </MenuItem>
          ))}
        </Select>

        <input
          type="tel"
          value={profileData[field] || ""}
          onChange={(e) => handleInputChange(field, e.target.value)}
          disabled={isPrimary}
          placeholder={`Enter ${label}`}
          className={`flex-1 px-3 py-2 rounded-md border border-[#EAEBEB] focus:outline-none bg-[#F4F5F6]
            ${
              isPrimary
                ? "cursor-not-allowed text-gray-400"
                : "text-[#9D0385] font-bold"
            }`}
        />
      </div>
    </div>
  );

  const renderRadio = (
    label,
    field,
    options,
    currentValue = profileData[field]
  ) => (
    <div className="mb-4 font-altisunisa">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <div className="flex gap-6">
        {options.map((opt) => (
          <label key={opt} className="flex items-center">
            <input
              type="radio"
              name={field}
              value={opt}
              checked={currentValue !== null && Number(currentValue) === opt}
              onChange={(e) => handleInputChange(field, Number(e.target.value))}
              className="mr-2"
              style={{ accentColor: "#9D0385" }}
            />
            {genderMap[opt]}
          </label>
        ))}
      </div>
    </div>
  );

  // const renderRadio = (label, field, options, currentValue = profileData[field]) => (
  //   <div className="mb-4 font-altisunisa">
  //     <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
  //     <div className="flex gap-6">
  //       {options.map((opt) => (
  //         <label key={opt} className="flex items-center">
  //           <input
  //             type="radio"
  //             name={field}
  //             value={opt}
  //             checked={currentValue === opt}
  //             onChange={(e) => handleInputChange(field, e.target.value)}
  //             className="mr-2 "
  //             style={{ accentColor: "#9D0385" }}
  //           />
  //           {opt}
  //         </label>
  //       ))}
  //     </div>
  //   </div>
  // );

  const renderMaritalStatus = () => (
    <div className="mb-4 font-altisunisa">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Marital Status
      </label>
      <div className="flex flex-wrap gap-4">
        {maritalStatusList.map((status) => (
          <label key={status.id} className="flex items-center">
            <input
              type="radio"
              name="maritial_status"
              value={status.id}
              checked={Number(profileData.maritial_status) === status.id}
              onChange={(e) =>
                handleInputChange("maritial_status", Number(e.target.value))
              }
              className="mr-2 "
              style={{ accentColor: "#9D0385" }}
            />
            {status.maritial_type}
          </label>
        ))}
      </div>
    </div>
  );

  const renderNationalitySelect = () => (
    <div className="mb-4 font-altisunisa">
      <label className="block text-sm font-medium text-gray-700">
        Nationality <span className="text-red-500">*</span>
      </label>
      <select
        value={profileData.nationality_id || ""}
        onChange={(e) => handleInputChange("nationality_id", e.target.value)}
        className="w-full px-3 py-2 rounded-md border border-[#EAEBEB] text-[#9D0385] font-bold bg-[#F4F5F6]"
      >
        <option value="">Select Nationality</option>
        {nationalityList.map((n) => (
          <option key={n.id} value={n.id}>
            {n.nationality}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div>
      <h2 className="text-lg font-medium text-black mb-2 font-siromi">
        Basic Information
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {renderInput("First Name", "first_name", "text", true)}
        {renderInput("Last Name", "last_name")}
        {renderInput("Date of Birth", "date_of_birth", "date", true)}
        {renderNationalitySelect()}
        {renderInput("Email", "email", "email", true)}
        {/* Mobile - primary not editable */}
        {renderPhoneField("Primary Mobile", "mobile", true)}
        {renderPhoneField("Alternate Mobile", "secondary_mobile", false)}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {renderRadio("Gender", "gender", [0, 1, 2], profileData.gender)}

        {renderMaritalStatus()}
      </div>

      <h2 className="text-lg font-medium text-black mt-6 mb-2 font-siromi">
        Residence
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="mb-4 font-altisunisa">
          <label className="block text-sm font-medium">
            Country <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedCountry}
            onChange={handleCountryChange}
            className="w-full px-3 py-2 rounded-md border border-[#EAEBEB] bg-[#F4F5F6] text-[#9D0385] font-bold"
          >
            <option value="">Select Country</option>
            {countries.map((c) => (
              <option key={c.id} value={c.id}>
                {c.country_name}
              </option>
            ))}
          </select>
        </div>
        <div className="mb-4 font-altisunisa">
          <label className="block text-sm font-medium">
            Postal/Zip code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={profileData.zip_code || ""}
            onChange={(e) => handleInputChange("zip_code", e.target.value)}
            onBlur={handleZipBlur} // ✅ Directly attached
            placeholder="Enter Postal/Zip code"
            className="w-full px-3 py-2 rounded-md border border-[#EAEBEB] bg-[#F4F5F6] text-[#9D0385] font-bold"
          />
        </div>

        {/* State */}
        <div className="mb-4 font-altisunisa">
          <label className="block text-sm font-medium">
            State <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedState}
            onChange={handleStateChange}
            className="w-full px-3 py-2 rounded-md border border-[#EAEBEB] bg-[#F4F5F6] text-[#9D0385] font-bold"
          >
            <option value="">Select State</option>
            {states.map((s) => (
              <option key={s.id} value={s.id}>
                {s.state_name}
              </option>
            ))}
          </select>
        </div>

        {/* City */}
        <div className="mb-4 font-altisunisa">
          <label className="block text-sm font-medium">City</label>
          <select
            value={selectedCity}
            onChange={handleCityChange}
            className="w-full px-3 py-2 rounded-md border border-[#EAEBEB] bg-[#F4F5F6] text-[#9D0385] font-bold"
          >
            <option value="">Select City</option>
            {cities.map((ct) => (
              <option key={ct.id} value={ct.id}>
                {ct.city_name}
              </option>
            ))}
          </select>
        </div>

        {renderInput("Street Address", "address")}
      </div>

      <h2 className="text-lg font-medium text-black mt-6 mb-2 font-siromi">
        Advanced Information{" "}
        <span className="text-sm text-gray-500">(Optional)</span>
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="mb-4 font-altisunisa">
          <label className="block text-sm font-medium text-gray-700">
            Passport Number
          </label>
          <input
            type="text"
            value={profileData.passport_number || ""}
            onChange={(e) =>
              handleInputChange("passport_number", e.target.value.toUpperCase())
            }
            placeholder="Enter Passport Number"
            className={`w-full px-3 py-2 rounded-md border border-[#EAEBEB] bg-[#F4F5F6] text-[#9D0385] font-bold `}
          />
        </div>
        {renderInput("Passport Expiry", "passport_expiry", "date")}
      </div>

      <div className="flex justify-end gap-4 mt-6">
        <button
          onClick={() => setIsEditing(false)}
          className="px-6 py-3 border border-gray-300 rounded-2xl"
        >
          Cancel
        </button>
        <button
          onClick={() => setShowConfirm(true)}
          className="px-6 py-3 bg-[#572575] text-white rounded-2xl"
        >
          Save
        </button>
      </div>
      {/* Snackbar */}
      <AnimatedSnackbar
        open={toast.open}
        onClose={closeToast}
        message={toast.message}
        severity={toast.severity}
      />
      <ConfirmDialog
        open={showConfirm}
        title="Confirm Save"
        message={`Are you sure you want to save your Primary details?`}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
      <SuccessDialog
        open={showSuccess}
        message="Your details have been saved successfully."
        onClose={() => setShowSuccess(false)}
        onSuccess={() => {
          setIsEditing(false); // exit edit mode only after success ack
          if (onSaveSuccess) onSaveSuccess();
        }}
      />
    </div>
  );
};

const PrimaryInformation = ({
  profileData,
  isEditing,
  setIsEditing,
  handleInputChange,
  onSaveSuccess,
}) =>
  isEditing ? (
    <PrimaryInformationEdit
      profileData={profileData}
      setIsEditing={setIsEditing}
      handleInputChange={handleInputChange}
      onSaveSuccess={onSaveSuccess}
    />
  ) : (
    <PrimaryInformationView
      profileData={profileData}
      setIsEditing={setIsEditing}
    />
  );

export default PrimaryInformation;
